/**
 * The note-card layout's shape of a thread: one card per top-level entry,
 * each holding the replies that answer it, in chronological order. The server
 * collapses a reply-to-a-reply onto its root, so `reply_to` normally names a
 * top-level entry; a chain is still followed up to its root, defensively.
 */
import { isRejectionEntry, type ChatThreadEntry } from './chatEntries';

export interface NoteCardGroup {
  entry: ChatThreadEntry;
  replies: ChatThreadEntry[];
}

/** A rejection is a system entry, not a card — nothing nests under it, and it never nests. */
function canNest(entry: ChatThreadEntry): boolean {
  return !isRejectionEntry(entry);
}

/** The loaded parent a reply nests under; none when the parent isn't loaded (the reply is then its own card). */
function loadedParent(entry: ChatThreadEntry, byId: ReadonlyMap<string, ChatThreadEntry>): ChatThreadEntry | undefined {
  if (!entry.reply_to || !canNest(entry)) return undefined;
  const parent = byId.get(entry.reply_to);
  if (!parent || parent.id === entry.id || !canNest(parent)) return undefined;
  return parent;
}

/**
 * The id of the top-level entry a message belongs under; its own when it's
 * top-level. Malformed data that loops back on itself is shown as its own
 * card rather than dropped.
 */
function rootIdOf(entry: ChatThreadEntry, byId: ReadonlyMap<string, ChatThreadEntry>): string {
  const seen = new Set<string>([entry.id]);
  let current = entry;
  for (let parent = loadedParent(current, byId); parent; parent = loadedParent(current, byId)) {
    if (seen.has(parent.id)) return entry.id;
    seen.add(parent.id);
    current = parent;
  }
  return current.id;
}

/** Groups an already chronologically sorted thread into top-level cards with their replies. */
export function groupNoteCards(sorted: ChatThreadEntry[]): NoteCardGroup[] {
  const byId = new Map(sorted.map((entry) => [entry.id, entry] as const));
  const rootIds = new Map(sorted.map((entry) => [entry.id, rootIdOf(entry, byId)] as const));
  const groups = new Map<string, NoteCardGroup>();
  for (const entry of sorted) {
    if (rootIds.get(entry.id) === entry.id) groups.set(entry.id, { entry, replies: [] });
  }
  for (const entry of sorted) {
    const rootId = rootIds.get(entry.id);
    if (!rootId || rootId === entry.id) continue;
    groups.get(rootId)?.replies.push(entry);
  }
  return [...groups.values()];
}
